import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { GridWithCulling } from '@ff-os/shared-react/components/grid-with-culling/GridWithCulling';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { useMobile } from '@ff-os/shared-react/hooks/useMobile';
import { useSize } from '@ff-os/shared-react/hooks/useSize';
import { Size } from '@ff-os/shared-utils/types/Size';
import { selectPhotoIdsByGroupId } from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { range } from 'lodash';
import { selectAmountInQueueByGroupId } from '../_redux-groups';
import { PhotoPrintErrorWrapper } from './photo-print/PhotoPrintErrorWrapper';
import { PhotoPrintLoader } from './photo-print/PhotoPrintLoader';
import { PhotoPrintSpinner } from './photo-print/PhotoPrintSpinner';
import styles from './PhotoPrintsGrid.module.scss';

const minimumWidth = 175;

const gapSize = 16; // Keep in sync with GridWithCulling gap property

// Keep in sync with PhotoPrintQuantity styles
const quantityInputHeight = 36;
const quantityInputMargins = 16;

const cellHeightOffset = quantityInputHeight + quantityInputMargins;

const _PhotoPrintsGrid = ({ amountInQueue, photoIds, renderPhoto }: Props) => {

    const container = useRef<HTMLDivElement>(null);
    const containerSize = useSize(container);

    const [scroll, setScroll] = useState<number>(0);

    const { cellSize, columnCount } = useMemo(
        () => determineLayout(containerSize),
        [containerSize]);

    const isMobile = useMobile();

    return (
        <div className={styles.container}
            style={{ minHeight: determineMinHeight(photoIds.length, columnCount, cellSize.height) }}
            ref={container}>
            <GridWithCulling gap='s'
                columns={columnCount} expectedCellHeight={cellSize.height}
                scroll={scroll} onScroll={setScroll}>

                {photoIds.map(id => (
                    <Cell key={id}>
                        <PhotoPrintErrorWrapper id={id} isMobile={isMobile}>
                            <PhotoPrintLoader id={id}>
                                {renderPhoto(id)}
                            </PhotoPrintLoader>
                        </PhotoPrintErrorWrapper>
                    </Cell>
                ))}

                {range(0, amountInQueue).map(i => (
                    <Cell key={i}>
                        <PhotoPrintSpinner />
                    </Cell>
                ))}
            </GridWithCulling>
        </div>
    );
};

type OwnProps = {
    groupId: string;
    renderPhoto: (id: string) => JSX.Element;
};

type Props = OwnProps & {
    photoIds: string[];
    amountInQueue: number;
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => ({
    amountInQueue: selectAmountInQueueByGroupId(state, ownProps.groupId),
    photoIds: selectPhotoIdsByGroupId(state, ownProps.groupId)
});

export const PhotoPrintsGrid = connect(
    mapStateToProps
)(_PhotoPrintsGrid);

const columns = [4, 3, 2] as const;

type Layout = {
    columnCount: typeof columns[number];
    cellSize: Size;
};

const determineMinHeight = (
    amountOfPhotos: number,
    columnCount: number,
    cellHeight: number
) => {
    return amountOfPhotos <= columnCount
        ? cellHeight
        : 2 * cellHeight + gapSize;
};

const determineLayout = (containerSize: Size | undefined): Layout => {

    if (containerSize) {
        for (const columnCount of columns) {
            const columnWidth = calculateColumnWidth(columnCount, containerSize);
            const cellSize = estimateCellSize(columnWidth);
            if (cellSize.width >= minimumWidth) {
                return { columnCount, cellSize };
            }
        }
    }

    return {
        columnCount: 2,
        cellSize: estimateCellSize(minimumWidth)
    };
};

const calculateColumnWidth = (columnCount: number, containerSize: Size) => {
    const totalGapSize = (columnCount - 1) * gapSize;
    return (containerSize.width - totalGapSize) / columnCount;
};

const estimateCellSize = (width: number) => ({
    width: width,
    height: width + cellHeightOffset
});